(() => {
  const rootSelector = '[data-sw-materials]';

  function initialize(root) {
    if (!root || root.dataset.swMaterialsReady === 'true') return;
    root.dataset.swMaterialsReady = 'true';

    const seriesSelect = root.querySelector('[data-sw-material-series]');
    const materialSelect = root.querySelector('[data-sw-material-option]');
    const player = root.querySelector('[data-sw-material-player]');
    const playerStage = root.querySelector('[data-sw-material-player-stage]');
    const playbackToggle = root.querySelector('[data-sw-material-playback-toggle]');
    const status = root.querySelector('[data-sw-material-status]');
    const description = root.querySelector('[data-sw-material-description]');

    if (!seriesSelect || !materialSelect || !player || !playerStage || !playbackToggle) return;

    const normalizeHandle = (value) => (value || '').trim().toLowerCase();
    const compactHandle = (value) => normalizeHandle(value).replace(/[^a-z0-9]/g, '');

    const selectByHandle = (select, dataKey, requestedHandle) => {
      const normalizedHandle = normalizeHandle(requestedHandle);
      if (!normalizedHandle) return false;
      const compactRequestedHandle = compactHandle(normalizedHandle);

      const options = Array.from(select.options);
      const matchingOption =
        options.find(
          (option) => normalizeHandle(option.dataset[dataKey]) === normalizedHandle
        ) ||
        options.find(
          (option) => compactHandle(option.dataset[dataKey]) === compactRequestedHandle
        ) ||
        options.find((option) => {
          const compactOptionHandle = compactHandle(option.dataset[dataKey]);
          return (
            compactOptionHandle &&
            compactRequestedHandle &&
            (compactOptionHandle.startsWith(compactRequestedHandle) ||
              compactRequestedHandle.startsWith(compactOptionHandle))
          );
        });

      if (!matchingOption) return false;
      select.value = matchingOption.value;
      return true;
    };

    const getSeriesTemplate = (seriesId) =>
      root.querySelector(
        `template[data-sw-material-series-options="${CSS.escape(seriesId)}"]`
      );

    const updatePlaybackToggle = () => {
      const isPlaying = !player.paused && !player.ended;
      playbackToggle.classList.toggle('is-playing', isPlaying);
      playbackToggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      playbackToggle.setAttribute('aria-pressed', String(isPlaying));
    };

    const togglePlayback = () => {
      if (player.paused || player.ended) {
        const playPromise = player.play();
        playPromise?.catch?.(updatePlaybackToggle);
      } else {
        player.pause();
      }
    };

    const syncUrl = () => {
      if (!window.history?.replaceState) return;

      const seriesHandle = seriesSelect.selectedOptions[0]?.dataset.seriesHandle;
      const materialHandle = materialSelect.selectedOptions[0]?.dataset.materialHandle;
      if (!seriesHandle || !materialHandle) return;

      const url = new URL(window.location.href);
      url.searchParams.set('series', seriesHandle);
      url.searchParams.set('material', materialHandle);
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    };

    const setPlayer = ({ autoplay = false, updateUrl = false } = {}) => {
      const option = materialSelect.selectedOptions[0];
      if (!option) return;

      const seriesName =
        seriesSelect.selectedOptions[0]?.textContent?.trim() || 'Material series';
      const materialName = option.textContent.trim();
      const poster = option.dataset.poster || '';
      const materialDescription = option.dataset.description?.trim() || '';

      player.pause();
      player.setAttribute('aria-label', `${seriesName} — ${materialName}`);

      if (option.value) {
        playbackToggle.disabled = false;
        player.poster = poster;
        player.src = option.value;
        player.load();
        playerStage.hidden = false;
      } else {
        playbackToggle.disabled = true;
        player.removeAttribute('src');
        player.removeAttribute('poster');
        player.load();
        playerStage.hidden = true;
      }

      updatePlaybackToggle();

      if (status) status.textContent = `${seriesName} · ${materialName}`;

      if (description) {
        description.textContent = materialDescription;
        description.hidden = !materialDescription;
      }

      if (updateUrl) syncUrl();

      if (autoplay && option.value) {
        const playPromise = player.play();
        playPromise?.catch?.(updatePlaybackToggle);
      }
    };

    const setSeries = ({ autoplay = false, materialHandle = '', updateUrl = false } = {}) => {
      const template = getSeriesTemplate(seriesSelect.value);
      if (!template) return;

      materialSelect.replaceChildren(template.content.cloneNode(true));
      selectByHandle(materialSelect, 'materialHandle', materialHandle);
      materialSelect.disabled = materialSelect.options.length < 2;
      setPlayer({ autoplay, updateUrl });
    };

    seriesSelect.addEventListener('change', () => {
      setSeries({ autoplay: true, updateUrl: true });
    });

    materialSelect.addEventListener('change', () => {
      setPlayer({ autoplay: true, updateUrl: true });
    });

    player.addEventListener('click', togglePlayback);
    playbackToggle.addEventListener('click', togglePlayback);
    ['play', 'pause', 'ended', 'emptied', 'error'].forEach((eventName) => {
      player.addEventListener(eventName, updatePlaybackToggle);
    });

    const searchParams = new URLSearchParams(window.location.search);
    const requestedSeriesHandle = searchParams.get('series');
    const requestedMaterialHandle = searchParams.get('material');
    const hasRequestedSelection = Boolean(requestedSeriesHandle || requestedMaterialHandle);

    selectByHandle(seriesSelect, 'seriesHandle', requestedSeriesHandle);
    setSeries({
      materialHandle: requestedMaterialHandle,
      updateUrl: hasRequestedSelection,
    });
  }

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initialize);
  };

  initializeAll();

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
